<template>
  <div class="demo-step">
    <div class="row g-4">
      <div class="col-lg-5 order-2 order-lg-1">
        <SolderaProofComponent teamAsNames />
      </div>

      <div class="col-lg-7 order-1 order-lg-2">
        <p class="step-eyebrow">{{ $t("billing.demo.eyebrow") }}</p>
        <h2 class="demo-title font-serif mb-2">
          {{ $t("billing.demo.title") }}
        </h2>
        <p class="text-body-secondary mb-4">
          {{ $t("billing.demo.subtitle") }}
        </p>

        <ul class="list-unstyled mb-4">
          <li
            v-for="point in POINTS"
            :key="point"
            class="demo-point d-flex align-items-start gap-2"
            :class="{ 'is-highlight': point === 'account_executive' }"
          >
            <FontAwesomeIcon
              :icon="point === 'account_executive' ? 'user-tie' : 'check'"
              class="demo-icon mt-1"
              fixed-width
            />
            <span>{{ $t(`billing.demo.points.${point}`) }}</span>
          </li>
        </ul>

        <div
          v-if="requested"
          class="requested-box d-flex align-items-start gap-3 mb-3"
          data-testid="demo-requested"
        >
          <FontAwesomeIcon icon="circle-check" class="requested-icon mt-1" />
          <div>
            <p class="fw-semibold mb-1">
              {{ $t("billing.demo.requested_title") }}
            </p>
            <p class="small mb-0">{{ $t("billing.demo.requested_body") }}</p>
          </div>
        </div>

        <button
          type="button"
          class="btn demo-button w-100"
          :class="requested ? 'btn-outline-primary' : 'btn-primary'"
          :disabled="loading"
          data-testid="book-demo-button"
          @click="$emit('book')"
        >
          <span
            v-if="loading"
            class="spinner-border spinner-border-sm me-2"
            role="status"
            aria-hidden="true"
          ></span>
          <FontAwesomeIcon v-else icon="calendar-check" class="me-2" />
          {{
            requested ? $t("billing.demo.book_again") : $t("billing.demo.cta")
          }}
        </button>
        <p class="small text-body-secondary text-center mt-2">
          {{ $t("billing.demo.booked_note") }}
        </p>

        <button
          type="button"
          class="btn btn-link p-0 small"
          data-testid="change-answers"
          @click="$emit('back')"
        >
          {{ $t("billing.plan.change_answers") }}
        </button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import SolderaProofComponent from "@/billing/SolderaProofComponent.vue";

const POINTS = [
  "account_executive",
  "strategy",
  "pricing",
  "reporting",
] as const;

withDefaults(
  defineProps<{
    loading?: boolean;
    /** The booking page was opened: the dashboard now waits on sales to agree the deal. */
    requested?: boolean;
  }>(),
  { loading: false, requested: false }
);

defineEmits<{
  book: [];
  back: [];
}>();
</script>

<style scoped lang="scss">
.step-eyebrow {
  font-size: 0.6875rem;
  font-weight: 600;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--phoenix-primary);
  margin-bottom: 0.5rem;
}

.demo-title {
  font-size: 2rem;
  font-weight: 600;
  line-height: 1.15;
  letter-spacing: -0.01em;
}

.demo-point {
  margin-bottom: 0.75rem;

  &.is-highlight {
    padding: 0.75rem 0.9rem;
    border-radius: 6px;
    background: #faecca;
    font-weight: 600;
    color: #5d3f06;

    .demo-icon {
      color: #c08e3a;
    }
  }
}

.demo-icon {
  color: var(--phoenix-primary);
  flex: 0 0 auto;
}

.requested-box {
  padding: 0.9rem 1rem;
  border-radius: 6px;
  border: 1px solid #b8d0bf;
  background: #eef4ef;
}

.requested-icon {
  color: var(--phoenix-primary);
  font-size: 1.25rem;
}

.demo-button {
  min-height: 56px;
  border-radius: 6px;
  font-weight: 600;
}
</style>
